import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Search } from 'lucide-react';
import { useCallback, useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router';
import { fetchAllPages } from '../api/client';
import { useApi } from '../api/context';
import { PAGE_SIZE, queryKeys } from '../api/queries';
import { useAuth, usePermission } from '../auth/context';
import { useDockCommands } from '../dock/commands';
import { strings } from '../strings';
import { Keys } from './Keys';
import { useToast } from '../components/toast';
import {
  filterPalette,
  groupLabel,
  PALETTE_GROUPS,
  PALETTE_LIMIT,
  type NetworkCommand,
  type PaletteEntry,
} from './palette';
import { useRouteEntities } from './routeContext';
import { focusRegion, shortcutKeys, shellShortcuts } from './shortcuts';
import { useTheme } from './theme';

const p = strings.shell.palette;

const n = strings.networks.nav;

function usePaletteEntries(
  close: () => void,
  openShortcuts: () => void,
  openChooser: (command: NetworkCommand) => void,
): PaletteEntry[] {
  const api = useApi();
  const navigate = useNavigate();
  const { signOut, config } = useAuth();
  const networking = config.networking?.enabled ?? false;
  const canOperate = usePermission('operate');
  const canManageNetworks = usePermission('manage-networks');
  const { effective, setPreference } = useTheme();
  const { blueprintId } = useRouteEntities();
  const dockCommands = useDockCommands();

  const blueprints = useQuery({
    queryKey: queryKeys.blueprints(null),
    queryFn: () => api.listBlueprints({ limit: PAGE_SIZE, cursor: null }),
  });
  const deployments = useQuery({
    queryKey: queryKeys.allDeployments(blueprintId ?? ''),
    queryFn: () => fetchAllPages((page) => api.listDeployments(blueprintId!, page)),
    enabled: blueprintId !== null,
  });

  return useMemo(() => {
    const go = (path: string) => () => navigate(path);
    const sequence = (id: string) => shellShortcuts(networking).find((s) => s.id === id);
    const entries: PaletteEntry[] = [
      {
        id: 'go-blueprints',
        group: 'goTo',
        label: p.goBlueprints,
        keys: sequence('go-blueprints')?.keys[0],
        sequence: true,
        run: go('/blueprints'),
      },
      {
        id: 'go-deployments',
        group: 'goTo',
        label: p.goDeployments,
        keys: sequence('go-deployments')?.keys[0],
        sequence: true,
        run: go('/deployments'),
      },
      // Feature spec 17: while networking is on.
      ...(networking
        ? [
            {
              id: 'go-networks',
              group: 'goTo' as const,
              label: strings.networks.nav.networks,
              keys: sequence('go-networks')?.keys[0],
              sequence: true,
              run: go('/networks'),
            },
          ]
        : []),
      {
        id: 'go-runs',
        group: 'goTo',
        label: p.goRuns,
        keys: sequence('go-runs')?.keys[0],
        sequence: true,
        run: go('/runs'),
      },
    ];
    if (canOperate) {
      entries.push({
        id: 'go-cloud-accounts',
        group: 'goTo',
        label: p.goCloudAccounts,
        keys: sequence('go-cloud-accounts')?.keys[0],
        sequence: true,
        run: go('/cloud-accounts'),
      });
    }
    entries.push({ id: 'go-about', group: 'goTo', label: p.goAbout, run: go('/about') });
    entries.push({ id: 'go-whats-new', group: 'goTo', label: p.goWhatsNew, run: go('/whats-new') });
    for (const blueprint of blueprints.data?.items ?? []) {
      entries.push({
        id: `blueprint-${blueprint.id}`,
        group: 'blueprints',
        label: blueprint.name,
        run: go(`/blueprints/${blueprint.id}`),
      });
    }
    if (blueprintId) {
      for (const deployment of deployments.data ?? []) {
        entries.push({
          id: `deployment-${deployment.id}`,
          group: 'deployments',
          label: deployment.name,
          run: go(`/deployments/${deployment.id}`),
        });
      }
    }
    // Feature spec 17: the network commands continue with the network chooser.
    if (networking) {
      entries.push({
        id: 'network-go',
        group: 'commands',
        label: n.goToNetwork,
        keepOpen: true,
        run: () => openChooser('go'),
      });
      if (canManageNetworks)
        entries.push({
          id: 'network-plan',
          group: 'commands',
          label: n.planNetwork,
          keepOpen: true,
          run: () => openChooser('plan'),
        });
      if (canOperate || canManageNetworks)
        entries.push({
          id: 'network-attach',
          group: 'commands',
          label: n.attachToNetwork,
          keepOpen: true,
          run: () => openChooser('attach'),
        });
    }
    entries.push(
      { id: 'theme-system', group: 'commands', label: p.themeSystem, run: () => setPreference('system') },
      { id: 'theme-light', group: 'commands', label: p.themeLight, run: () => setPreference('light') },
      { id: 'theme-dark', group: 'commands', label: p.themeDark, run: () => setPreference('dark') },
      {
        id: 'theme-toggle',
        group: 'commands',
        label: p.toggleTheme,
        run: () => setPreference(effective === 'dark' ? 'light' : 'dark'),
      },
      {
        id: 'shortcuts',
        group: 'commands',
        label: p.shortcuts,
        keys: shortcutKeys('help'),
        run: openShortcuts,
      },
      {
        id: 'next-region',
        group: 'commands',
        label: p.nextRegion,
        keys: shortcutKeys('next-region'),
        // After the palette has returned focus to where it was.
        run: () => setTimeout(() => focusRegion(1), 0),
      },
    );
    entries.push(...dockCommands);
    if (signOut) entries.push({ id: 'sign-out', group: 'commands', label: p.signOut, run: signOut });
    return entries.map((entry) => ({
      ...entry,
      run: entry.keepOpen
        ? entry.run
        : () => {
            close();
            entry.run();
          },
    }));
  }, [
    navigate,
    canOperate,
    canManageNetworks,
    networking,
    openChooser,
    blueprints.data,
    deployments.data,
    blueprintId,
    effective,
    setPreference,
    openShortcuts,
    signOut,
    dockCommands,
    close,
  ]);
}

/** The command palette (spec 6.1.11): a modal combobox with grouped entries. */
export function CommandPalette({
  onClose,
  openShortcuts,
}: {
  onClose: () => void;
  openShortcuts: () => void;
}) {
  const api = useApi();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  // The network chooser step (feature spec 17): the networks of the first page replace the entries.
  const [chooser, setChooser] = useState<NetworkCommand | null>(null);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const openChooser = useCallback((command: NetworkCommand) => {
    setChooser(command);
    setQuery('');
    setActive(0);
  }, []);
  const entries = usePaletteEntries(onClose, openShortcuts, openChooser);
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useId();
  const optionId = (index: number) => `${listId}-option-${index}`;
  const networks = useQuery({
    queryKey: queryKeys.networks(null),
    queryFn: () => api.listNetworks({ limit: PAGE_SIZE, cursor: null }),
    enabled: chooser !== null,
  });
  const chooseNetwork = (id: string) => {
    const command = chooser;
    onClose();
    if (command === 'go') navigate(`/networks/${id}`);
    else if (command === 'attach') navigate(`/networks/${id}?attach=1`);
    else if (command === 'plan')
      api
        .createNetworkRun(id, { kind: 'plan' })
        .then((run) => {
          void queryClient.invalidateQueries({ queryKey: ['network', id] });
          void queryClient.invalidateQueries({ queryKey: ['runs'] });
          navigate(`/runs/${run.id}`);
        })
        .catch((error: unknown) => toast.error(error));
  };
  const backToEntries = () => {
    setChooser(null);
    setQuery('');
    setActive(0);
  };

  const results = useMemo<PaletteEntry[]>(() => {
    if (chooser === null) return filterPalette(entries, query);
    const words = query.toLocaleLowerCase().split(/\s+/).filter(Boolean);
    return (networks.data?.items ?? [])
      .filter((network) => words.every((word) => network.name.toLocaleLowerCase().includes(word)))
      .slice(0, PALETTE_LIMIT)
      .map((network) => ({
        id: `network-${network.id}`,
        group: 'commands' as const,
        label: network.name,
        run: () => chooseNetwork(network.id),
      }));
    // chooseNetwork changes with every render; the list only with its inputs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entries, query, chooser, networks.data]);
  const activeIndex = results.length === 0 ? -1 : Math.min(active, results.length - 1);

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    inputRef.current?.focus();
    return () => previous?.focus?.();
  }, []);

  useEffect(() => {
    if (activeIndex >= 0)
      document.getElementById(optionId(activeIndex))?.scrollIntoView?.({ block: 'nearest' });
  });

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    const count = results.length;
    const move = (to: number) => {
      event.preventDefault();
      if (count > 0) setActive((to + count) % count);
    };
    switch (event.key) {
      case 'ArrowDown':
        move(activeIndex + 1);
        break;
      case 'ArrowUp':
        move(activeIndex - 1);
        break;
      case 'Home':
        if (count > 0 && !event.shiftKey) move(0);
        break;
      case 'End':
        if (count > 0 && !event.shiftKey) move(count - 1);
        break;
      case 'Enter':
        event.preventDefault();
        if (activeIndex >= 0) results[activeIndex].run();
        break;
      case 'Escape':
        event.preventDefault();
        event.stopPropagation();
        if (chooser !== null) backToEntries();
        else onClose();
        break;
      case 'Backspace':
        if (chooser !== null && query === '') {
          event.preventDefault();
          backToEntries();
        }
        break;
      case 'Tab':
        // The text field is the only stop in the dialog; the list is reached with the arrow keys.
        event.preventDefault();
        break;
    }
  };

  const numbered = results.map((entry, i) => ({ entry, i }));
  return (
    <div
      className="fixed inset-0 z-[60] flex items-start justify-center bg-[var(--scrim)] px-4 pt-[12vh]"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={p.title}
        className="flex max-h-[70vh] w-full max-w-[560px] flex-col overflow-hidden rounded-[10px] border border-line-strong bg-surface shadow-float"
      >
        <div className="flex items-center gap-2 border-b border-line px-3">
          <Search aria-hidden className="size-4 text-fg-muted" />
          <input
            ref={inputRef}
            role="combobox"
            aria-label={p.input}
            aria-expanded="true"
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={activeIndex >= 0 ? optionId(activeIndex) : undefined}
            className="h-11 min-w-0 flex-1 bg-transparent text-[14px] text-fg outline-none placeholder:text-fg-faint"
            placeholder={chooser !== null ? n.chooseNetwork : strings.shell.search}
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setActive(0);
            }}
            onKeyDown={onKeyDown}
          />
          <Keys keys={['Esc']} />
        </div>
        <div id={listId} role="listbox" aria-label={p.title} className="overflow-y-auto p-1.5">
          {results.length === 0 && (
            <p className="px-3 py-4 text-fg-muted">
              {chooser !== null && networks.isPending
                ? strings.common.loading
                : chooser !== null && networks.data?.items.length === 0
                  ? n.noNetworks
                  : p.noResults}
            </p>
          )}
          {chooser !== null && results.length > 0 && (
            <div role="group" aria-labelledby={`${listId}-networks`}>
              <div id={`${listId}-networks`} role="presentation" className="palette-group">
                {n.chooseNetwork}
              </div>
              {numbered.map(({ entry, i }) => (
                <div
                  key={entry.id}
                  id={optionId(i)}
                  role="option"
                  aria-selected={i === activeIndex}
                  className="palette-option"
                  onMouseMove={() => i !== activeIndex && setActive(i)}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => entry.run()}
                >
                  <span className="min-w-0 flex-1 truncate">{entry.label}</span>
                </div>
              ))}
            </div>
          )}
          {(chooser === null ? PALETTE_GROUPS : []).map((group) => {
            const inGroup = numbered.filter(({ entry }) => entry.group === group);
            if (inGroup.length === 0) return null;
            const headingId = `${listId}-${group}`;
            return (
              <div key={group} role="group" aria-labelledby={headingId}>
                <div id={headingId} role="presentation" className="palette-group">
                  {groupLabel(group)}
                </div>
                {inGroup.map(({ entry, i }) => {
                  return (
                    <div
                      key={entry.id}
                      id={optionId(i)}
                      role="option"
                      aria-selected={i === activeIndex}
                      className="palette-option"
                      onMouseMove={() => i !== activeIndex && setActive(i)}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => entry.run()}
                    >
                      <span className="min-w-0 flex-1 truncate">{entry.label}</span>
                      {entry.keys && <Keys keys={entry.keys} sequence={entry.sequence} />}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
